import {includes} from '@benchling/js-utils';
import React from 'react';
import ReactDOM from 'react-dom';

import DataConnector from 'connect/DataConnector';
import {EntityStoreUser} from 'constants/EntityStoreConstants';
import {Section, SECTIONS, SidepanelSection} from 'constants/NavConstants';
import HomeDashboardStore, {HomeDashboardStoreState} from 'home-dashboard/HomeDashboardStore';
import FullScreenStore from 'nav/full-screen/FullScreenStore';
import {showValueMessagingForSharedFile} from 'nav/share/growth/Util';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import SidepanelStore from 'nav/sidepanel/SidepanelStore';
import {ORGANIZATION_SCREEN} from 'orgs/OrganizationConstants';
import {renderToDOM} from 'redux-stores/ReduxDOMRender';
import EntityStore from 'stores/EntityStore';
import {ElementMouseEvent} from 'util/Types';

import NavBarContents from './NavBarContents';
import SettingsNavBarContents from './SettingsNavBarContents';
import SignedOutNavBarContents from './SignedOutNavBarContents';

interface ConnectedNavBarProps {
  currentUser: EntityStoreUser;
  homeDashboardState: HomeDashboardStoreState;
  isCreateMenuOpen: boolean;
  isOpen: boolean;
  newUserFlowOpen: boolean;
  organizationPageOpen: boolean;
  section: SidepanelSection | null;
}

export function initNavBar(): void {
  const navbarNode = document.getElementsByClassName('navbar-mount')[0];
  renderToDOM(<ConnectedNavBar />, navbarNode);
}
export function unmountNavBar(): void {
  const navbarNode = document.getElementsByClassName('navbar-mount')[0];
  ReactDOM.unmountComponentAtNode(navbarNode);
}

class NavBarConnector extends DataConnector<{}, ConnectedNavBarProps> {
  stores = [EntityStore, FullScreenStore, HomeDashboardStore, SidepanelStore];

  shouldSubscribeToRedux = false;

  enhanceProps(): ConnectedNavBarProps {
    const organizationPageOpen = this.fullScreenStore().getCurrentScreen() === ORGANIZATION_SCREEN;
    return {
      currentUser: this.entityStore().currentUser(),
      homeDashboardState: this.homeDashboardStore().getState(),
      isCreateMenuOpen: this.sidepanelStore().getIsCreateMenuOpen(),
      isOpen: this.sidepanelStore().getIsOpen(),
      section: this.sidepanelStore().getSection(),
      organizationPageOpen,
      newUserFlowOpen: this.fullScreenStore().isNewUserFlowActive(),
    };
  }
}

export class NavBar extends React.PureComponent<ConnectedNavBarProps> {
  setSection(section: Section): void {
    SidepanelActions.openSection(section);
  }

  _handleSectionClick(e: ElementMouseEvent, section: Section): void {
    if (this.props.isOpen && section === this.props.section) {
      SidepanelActions.close();
      return;
    }
    this.setSection(section);
  }

  render(): JSX.Element | null {
    if (!this.props.currentUser) {
      if (showValueMessagingForSharedFile()) {
        return null;
      }
      return <SignedOutNavBarContents />;
    } else if (this.props.section && !includes(Object.values(SECTIONS), this.props.section)) {
      return (
        <SettingsNavBarContents
          onClickHome={(e) => {
            this._handleSectionClick(e, Section.HOME);
          }}
        />
      );
    } else if (this.props.newUserFlowOpen) {
      // Don't show the NavBar in the signup flow
      return null;
    } else {
      return (
        <NavBarContents
          currentUser={this.props.currentUser}
          homeDashboardState={this.props.homeDashboardState}
          isCreateMenuOpen={this.props.isCreateMenuOpen}
          isUserIconActive={this.props.organizationPageOpen}
          onSectionClick={(e, section) => {
            this._handleSectionClick(e, section);
          }}
          section={this.props.section}
        />
      );
    }
  }
}

const ConnectedNavBar = NavBarConnector.connect(NavBar);

export default ConnectedNavBar;
